import React from "react";
import { Link } from "react-router-dom";
import {
  FileSpreadsheet,
  Calendar,
  LayoutGrid,
  UserPlus,
  CalendarDays,
  CalendarRange,
  BarChart3,
  Camera,
  Timer,
  Target,
  GitCompare,
  ShieldAlert,
} from "lucide-react";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";

const cards = [
  {
    to: "/payroll/attendance-images",
    title: "Punch photos",
    desc: "View employee selfie images from mobile punching (Firebase Storage).",
    icon: Camera,
  },
  {
    to: "/payroll/reports/employee-directory",
    title: "Employee directory",
    desc: "Preview all employees on your branches and download CSV.",
    icon: FileSpreadsheet,
  },
  {
    to: "/payroll/reports/tenure",
    title: "Joiners, leavers & anniversary",
    desc: "Filter by DOJ / leaving date; list anniversaries by DOJ month.",
    icon: UserPlus,
  },
  {
    to: "/payroll/reports/employee-attendance-card",
    title: "Monthly Status Report (Detailed Work Duration(Four Punch))",
    desc: "Monthly muster register for one employee: daily In/Out, duration, late, early, OT and summary.",
    icon: CalendarDays,
  },
  {
    to: "/payroll/reports/period-attendance",
    title: "Weekly & fortnightly attendance",
    desc: "Present days split by calendar week buckets and fortnights.",
    icon: BarChart3,
  },
  {
    to: "/payroll/yearly-attendance",
    title: "Yearly attendance",
    desc: "Present-day units and paid-leave day counts by month for a full calendar year.",
    icon: CalendarRange,
  },
  {
    to: "/payroll/reports/ot-summary",
    title: "OT summary (month)",
    desc: "Overtime and under-9h paid gap from punches (same rules as Daily hours).",
    icon: Timer,
  },
  {
    to: "/payroll/reports/shift-adherence",
    title: "Shift adherence",
    desc: "Late first punch vs expectedStartTime on employee docs.",
    icon: Target,
  },
  {
    to: "/payroll/reports/branch-comparison",
    title: "Branch comparison",
    desc: "Headcount and approximate present % per branch for a month.",
    icon: GitCompare,
  },
  {
    to: "/payroll/monthly-report",
    title: "Monthly report",
    desc: "All staff absent (no-punch) days and dates for a month — same rules as Monitor.",
    icon: Calendar,
  },
  {
    to: "/payroll/reports/attendance-audit",
    title: "Attendance audit log",
    desc: "What is needed for export/approval audit trails.",
    icon: ShieldAlert,
  },
];

export default function PayrollReportsHub() {
  return (
    <div className="min-h-screen bg-slate-100/90">
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Payroll</p>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="mt-1 text-sm text-slate-600">
            Extra payroll reports (CSV exports and summaries). Branch visibility follows Admin → branch access. These
            are also listed under <strong className="text-slate-800">Payroll</strong> in the top menu.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                className="group flex gap-4 rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm transition hover:border-indigo-300 hover:shadow-md"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                  <Icon className="h-6 w-6" aria-hidden />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900 group-hover:text-indigo-800">{c.title}</h2>
                  <p className="mt-1 text-sm text-slate-600">{c.desc}</p>
                  <span className="mt-2 inline-block text-xs font-semibold text-indigo-600">Open →</span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-4 text-sm text-slate-600">
          <div className="flex items-start gap-2">
            <LayoutGrid className="h-5 w-5 shrink-0 text-slate-400" aria-hidden />
            <div>
              <p className="font-semibold text-slate-800">Other payroll screens</p>
              <p className="mt-1">
                Use the Payroll menu for <strong>Monitor</strong>, <strong>Monthly report</strong>,{" "}
                <strong>Daily hours</strong>,{" "}
                <strong>Daily late report</strong>, <strong>Employees</strong>, etc.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
